import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

// Chart.js types
export interface ChartData {
  labels: string[];
  datasets: Array<{
    label?: string;
    data: number[];
    backgroundColor?: string | string[];
    borderColor?: string;
    borderWidth?: number;
    fill?: boolean;
    yAxisID?: string;
  }>;
}

export interface ChartOptions {
  responsive?: boolean;
  maintainAspectRatio?: boolean;
  indexAxis?: 'x' | 'y'; // 'y' renders a horizontal bar chart
  onClick?: any;
  onHover?: any;
  plugins?: {
    legend?: {
      display?: boolean;
      position?: 'top' | 'bottom' | 'left' | 'right';
    };
    tooltip?: any;
  };
  scales?: any;
}

interface ReportChartProps {
  type: 'bar' | 'line' | 'pie' | 'doughnut';
  data: ChartData;
  options?: ChartOptions;
  height?: number;
  width?: number;
}

export const ReportChart: React.FC<ReportChartProps> = ({
  type,
  data,
  options = {},
  height = 300,
  width
}) => {
  const [ChartComponent, setChartComponent] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadChart = async () => {
      try {
        // Dynamic imports for Chart.js
        const [
          chartjs,
          { Bar, Line, Pie, Doughnut }
        ] = await Promise.all([
          import('chart.js'),
          import('react-chartjs-2')
        ]);

        // Register Chart.js components
        chartjs.Chart.register(
          chartjs.CategoryScale,
          chartjs.LinearScale,
          chartjs.PointElement,
          chartjs.LineElement,
          chartjs.BarElement,
          chartjs.ArcElement,
          chartjs.Title,
          chartjs.Tooltip,
          chartjs.Legend
        );

        // Select the appropriate chart component
        const components: any = {
          bar: Bar,
          line: Line,
          pie: Pie,
          doughnut: Doughnut
        };

        setChartComponent(() => components[type]);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load Chart.js:', err);
        setError('Chart.js libraries not installed');
        setLoading(false);
      }
    };

    loadChart();
  }, [type]);

  const containerStyle = {
    height: `${height}px`,
    width: width ? `${width}px` : '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  if (loading) {
    return (
      <Box sx={containerStyle}>
        <CircularProgress size={24} />
        <Typography variant="body2" color="textSecondary" sx={{ ml: 1 }}>
          Loading chart...
        </Typography>
      </Box>
    );
  }

  if (error || !ChartComponent) {
    return (
      <Box sx={{ ...containerStyle, border: '1px dashed #ccc', borderRadius: '4px' }}>
        <Typography variant="body2" color="textSecondary">
          Chart ({type}) - Install chart.js and react-chartjs-2 to enable
        </Typography>
      </Box>
    );
  }

  const defaultOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        enabled: true,
      },
    },
    ...options
  };

  return (
    <Box sx={{ height: `${height}px`, width: width ? `${width}px` : '100%' }}>
      <ChartComponent data={data} options={defaultOptions} />
    </Box>
  );
};
